import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DiscoverRigs from './DiscoverRigs';
import touchPoseImage1 from '../../../assets/images/touch-pose1.png';
import touchPoseImage2 from '../../../assets/images/touch-pose2.png';
import imgActionsDropdowns from '../../../assets/images/docs/editor-actions-dropdowns.png';
import imgDmcGroups from '../../../assets/images/docs/unreal-dmc-groups.png';
import layersCardVideo from '../../../assets/video/home-card-layers.mp4';
import drawPoseCardVideo from '../../../assets/video/home-card-draw-pose.mp4';

/**
 * The tile's category, at the top of its text box.
 *
 * It began as a mono eyebrow, then briefly sat on the media, where it competed
 * with the screenshot for the same corner, then spent a while as a pill. It is
 * a mono eyebrow again, and the pill is not coming back: a pill reads as
 * something you can click, and this is a label for the words it introduces.
 * The chips further down stay pill-shaped, because they are a set and read as
 * one rather than as controls.
 */
const TileLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-3 font-mono text-[10.5px] uppercase tracking-[0.14em] text-blue">
    {children}
  </div>
);

/**
 * The tile in three or four words, for readers who will not read the sentence.
 * Every pill restates something the description already says — they are a
 * summary, never a place for a claim the copy does not make.
 */
const TileChips: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="flex flex-wrap gap-[6px] mt-4 p-0 m-0 list-none">
    {items.map((label) => (
      <li
        key={label}
        className="rounded-full border border-border-dark/[0.14] px-[9px] py-[3px] text-[11px] font-medium leading-[1.5] text-content-muted-alt"
      >
        {label}
      </li>
    ))}
  </ul>
);

const TouchPoseSection = () => {
  const { t } = useTranslation();
  const [lightboxImage, setLightboxImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  const [lightboxVideo, setLightboxVideo] = useState<{
    src: string;
    label: string;
  } | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLightboxImage(null);
        setLightboxVideo(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (lightboxImage || lightboxVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxImage, lightboxVideo]);

  const openLightbox = (src: string, alt: string) => {
    setLightboxImage({ src, alt });
  };

  const openVideoLightbox = (src: string, label: string) => {
    setLightboxVideo({ src, label });
  };

  return (
    <>
      <section className="bg-surface-base w-full">
        <div className="max-w-[1080px] mx-auto px-6 md:px-10 pt-24 pb-0">
          {/* Features header */}
          <div className="flex flex-col gap-3 mb-10">
            <div>
              <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-blue mb-3">
                {t('home.builtForAnimators')}
              </div>
              <h2 className="font-display font-bold text-[32px] md:text-[42px] tracking-[-0.03em] leading-[1.1] text-content-dark">
                {t('home.rigGetsOutOfTheWay')}
              </h2>
            </div>
            <p className="text-content-muted-alt text-sm md:max-w-[50%] leading-[1.65]">
              {t('home.rigDescription')}
            </p>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mb-20">
            {/* Wide card: text left, image right */}
            <div className="md:col-span-2 bg-surface-base border border-border-dark/[0.11] rounded-[18px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-250 hover:shadow-[0_12px_40px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 hover:border-[rgba(14,165,201,0.2)] relative group">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="p-8 md:p-10 lg:px-11 flex flex-col justify-center">
                  <TileLabel>{t('home.surfaceControls')}</TileLabel>
                  <h3 className="font-display font-bold text-[22px] md:text-[28px] tracking-[-0.02em] leading-[1.2] text-content-dark mb-2.5">
                    {t('home.removeClutter')}
                  </h3>
                  <p className="text-[13.5px] text-content-muted-alt leading-[1.65] max-w-xs">
                    {t('home.removeClutterDesc')}
                  </p>
                  <TileChips
                    items={[
                      t('home.tldrOnCharacter'),
                      t('home.tldrNoOutliner'),
                    ]}
                  />
                </div>
                <div className="min-h-[220px] md:min-h-[280px] bg-gradient-to-br from-[var(--characters-card-gradient-0)] to-[var(--characters-card-gradient-100)] flex items-center justify-center overflow-hidden relative">
                  <button
                    type="button"
                    onClick={() =>
                      openLightbox(touchPoseImage1, 'Surface controls screenshot')
                    }
                    className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2"
                  >
                    <img
                      src={touchPoseImage1}
                      alt="Surface controls screenshot"
                      className="w-full h-full object-cover"
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Small card: video top, text bottom — Layers */}
            <div className="bg-surface-base border border-border-dark/[0.11] rounded-[18px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-250 hover:shadow-[0_12px_40px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 hover:border-[rgba(14,165,201,0.2)] relative group flex flex-col">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
              <div className="h-[200px] bg-gradient-to-br from-[var(--characters-card-gradient-0)] to-[var(--characters-card-gradient-100)] flex items-center justify-center overflow-hidden shrink-0 relative">
                <button
                  type="button"
                  onClick={() =>
                    openVideoLightbox(layersCardVideo, t('home.layersTitle'))
                  }
                  className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2"
                  aria-label={t('home.watchFullScreen')}
                >
                  <video
                    src={layersCardVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover pointer-events-none"
                  />
                </button>
              </div>
              <div className="p-8 md:px-9 py-8 flex flex-col justify-end flex-1">
                <TileLabel>{t('home.layersLabel')}</TileLabel>
                <h3 className="font-display font-bold text-[22px] tracking-[-0.02em] leading-[1.2] text-content-dark mb-2.5">
                  {t('home.layersTitle')}
                </h3>
                <p className="text-[13.5px] text-content-muted-alt leading-[1.65] max-w-xs">
                  {t('home.layersDescription')}
                </p>
                <TileChips
                  items={[
                    t('home.tldrIndependentSets'),
                    t('home.tldrNoConflict'),
                    t('home.tldrSwitchInstantly'),
                  ]}
                />
              </div>
            </div>

            {/* Small card: video top, text bottom — Draw Pose */}
            <div className="bg-surface-base border border-border-dark/[0.11] rounded-[18px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-250 hover:shadow-[0_12px_40px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 hover:border-[rgba(14,165,201,0.2)] relative group flex flex-col">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
              <div className="h-[200px] bg-gradient-to-br from-[var(--characters-card-gradient-0)] to-[var(--characters-card-gradient-100)] flex items-center justify-center overflow-hidden shrink-0 relative">
                <button
                  type="button"
                  onClick={() =>
                    openVideoLightbox(drawPoseCardVideo, t('home.drawPoseTitle'))
                  }
                  className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2"
                  aria-label={t('home.watchFullScreen')}
                >
                  <video
                    src={drawPoseCardVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover pointer-events-none"
                  />
                </button>
              </div>
              <div className="p-8 md:px-9 py-8 flex flex-col justify-end flex-1">
                <TileLabel>{t('home.drawPoseLabel')}</TileLabel>
                <h3 className="font-display font-bold text-[22px] tracking-[-0.02em] leading-[1.2] text-content-dark mb-2.5">
                  {t('home.drawPoseTitle')}
                </h3>
                <p className="text-[13.5px] text-content-muted-alt leading-[1.65] max-w-xs">
                  {t('home.drawPoseDescription')}
                </p>
                <TileChips
                  items={[
                    t('home.tldrSketchViewport'),
                    t('home.tldrSolvesRig'),
                  ]}
                />
              </div>
            </div>

            {/* Small card: image top, text bottom */}
            <div className="bg-surface-base border border-border-dark/[0.11] rounded-[18px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-250 hover:shadow-[0_12px_40px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 hover:border-[rgba(14,165,201,0.2)] relative group flex flex-col">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
              <div className="h-[200px] bg-gradient-to-br from-[var(--characters-card-gradient-0)] to-[var(--characters-card-gradient-100)] flex items-center justify-center overflow-hidden shrink-0 relative">
                <button
                  type="button"
                  onClick={() =>
                    openLightbox(touchPoseImage2, 'Dynamic menus screenshot')
                  }
                  className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2"
                >
                  <img
                    src={touchPoseImage2}
                    alt="Dynamic menus screenshot"
                    className="w-full h-full object-contain"
                  />
                </button>
              </div>
              <div className="p-8 md:px-9 py-8 flex flex-col justify-end flex-1">
                <TileLabel>{t('home.contextMenus')}</TileLabel>
                <h3 className="font-display font-bold text-[22px] tracking-[-0.02em] leading-[1.2] text-content-dark mb-2.5">
                  {t('home.dynamicMenus')}
                </h3>
                <p className="text-[13.5px] text-content-muted-alt leading-[1.65] max-w-xs">
                  {t('home.dynamicMenusDesc')}
                </p>
                <TileChips
                  items={[
                    t('home.tldrScopedSelection'),
                    t('home.tldrRightClick'),
                  ]}
                />
              </div>
            </div>

            {/* Small card: image top, text bottom */}
            <div className="bg-surface-base border border-border-dark/[0.11] rounded-[18px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-250 hover:shadow-[0_12px_40px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 hover:border-[rgba(14,165,201,0.2)] relative group flex flex-col">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
              <div className="h-[200px] bg-gradient-to-br from-[var(--characters-card-gradient-0)] to-[var(--characters-card-gradient-100)] flex items-center justify-center overflow-hidden shrink-0 relative">
                <button
                  type="button"
                  onClick={() =>
                    openLightbox(
                      imgActionsDropdowns,
                      'Actions dropdowns in the TouchPose editor'
                    )
                  }
                  className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2"
                >
                  <img
                    src={imgActionsDropdowns}
                    alt="Actions dropdowns in the TouchPose editor"
                    className="w-full h-full object-cover object-top"
                  />
                </button>
              </div>
              <div className="p-8 md:px-9 py-8 flex flex-col justify-end flex-1">
                <TileLabel>{t('home.actionsLabel')}</TileLabel>
                <h3 className="font-display font-bold text-[22px] tracking-[-0.02em] leading-[1.2] text-content-dark mb-2.5">
                  {t('home.actionsTitle')}
                </h3>
                <p className="text-[13.5px] text-content-muted-alt leading-[1.65] max-w-xs">
                  {t('home.actionsDescription')}
                </p>
                <TileChips
                  items={[
                    t('home.tldrClicksModifiers'),
                    t('home.tldrAnyControl'),
                  ]}
                />
              </div>
            </div>

            {/* Wide card: text left, image right — TouchPose into Unreal's
                Direct Mesh Control. The shot is framed right of centre on
                purpose: the left third of the capture is the Paint PolyGroups
                panel, and the character carrying the groups is the part worth
                showing. To move to video later, swap the <img> for the same
                <video autoPlay muted loop playsInline> the layers card uses and
                keep this still as its poster — nothing else here changes. */}
            <div className="md:col-span-2 bg-surface-base border border-border-dark/[0.11] rounded-[18px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-250 hover:shadow-[0_12px_40px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 hover:border-[rgba(14,165,201,0.2)] relative group">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="p-8 md:p-10 lg:px-11 flex flex-col justify-center">
                  <TileLabel>{t('home.dmcLabel')}</TileLabel>
                  <h3 className="font-display font-bold text-[22px] md:text-[28px] tracking-[-0.02em] leading-[1.2] text-content-dark mb-2.5">
                    {t('home.dmcTitle')}
                  </h3>
                  <p className="text-[13.5px] text-content-muted-alt leading-[1.65] max-w-xs">
                    {t('home.dmcDescription')}
                  </p>
                  <TileChips
                    items={[
                      t('home.tldrIncluded'),
                      t('home.tldrNoCompiling'),
                      t('home.tldrExperimental'),
                    ]}
                  />
                  <p className="text-[11.5px] text-content-muted-light leading-[1.6] max-w-xs mt-3">
                    {t('home.dmcExperimental')}
                  </p>
                </div>
                <div className="min-h-[220px] md:min-h-[280px] bg-gradient-to-br from-[var(--characters-card-gradient-0)] to-[var(--characters-card-gradient-100)] flex items-center justify-center overflow-hidden relative">
                  <button
                    type="button"
                    onClick={() => openLightbox(imgDmcGroups, t('home.dmcAlt'))}
                    className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2"
                  >
                    <img
                      src={imgDmcGroups}
                      alt={t('home.dmcAlt')}
                      className="w-full h-full object-cover object-[72%_center]"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <DiscoverRigs />
      </section>

      {lightboxImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-surface-base/20 hover:bg-surface-base/30 text-surface-base text-2xl font-light transition-colors cursor-pointer"
            aria-label="Close image"
          >
            ×
          </button>
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            className="absolute inset-0 z-0 cursor-pointer"
            aria-hidden
          />
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            className="relative z-10 max-w-full max-h-[90vh] w-auto object-contain"
          />
        </div>
      )}

      {lightboxVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <button
            type="button"
            onClick={() => setLightboxVideo(null)}
            className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-surface-base/20 hover:bg-surface-base/30 text-surface-base text-2xl font-light transition-colors cursor-pointer"
            aria-label={t('home.close')}
          >
            ×
          </button>
          <button
            type="button"
            onClick={() => setLightboxVideo(null)}
            className="absolute inset-0 z-0 cursor-pointer"
            aria-hidden
          />
          <video
            src={lightboxVideo.src}
            controls
            autoPlay
            playsInline
            aria-label={lightboxVideo.label}
            className="relative z-10 max-w-full max-h-[90vh] w-auto rounded-[12px] shadow-2xl"
          />
        </div>
      )}
    </>
  );
};

export default TouchPoseSection;
